import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCurrency } from '../context/CurrencyContext';
import { useLanguage } from '../context/LanguageContext';
import { reviewsAPI } from '../utils/api';
import { Star } from 'lucide-react';

const generateSlug = (name) => name.toLowerCase().trim().replace(/[^\w\s-]/g, '').replace(/[-\s]+/g, '-').replace(/^-|-$/g, '');

const ProductCard = ({ product }) => {
  const { formatPrice } = useCurrency();
  const { t } = useLanguage();
  const [rating, setRating] = useState(null);
  const hasDiscount = product.discount_price && product.discount_price < product.price;
  const displayPrice = hasDiscount ? product.discount_price : product.price;
  const discountPercentage = hasDiscount ? Math.round(((product.price - product.discount_price) / product.price) * 100) : 0;
  const slug = generateSlug(product.name);

  useEffect(() => {
    reviewsAPI.getSummary(product.id).then(({ data }) => {
      if (data.total_reviews > 0) setRating(data);
    }).catch(() => {});
  }, [product.id]);

  return (
    <Link to={`/product/${product.id}/${slug}`} className="group cursor-pointer flex flex-col gap-4 border-none shadow-none bg-transparent" data-testid={`product-card-${product.id}`} title={`${product.name} - ${product.category} | Studio Crochet`}>
      <div className="relative aspect-[3/4] overflow-hidden bg-[#F3EFE6]">
        {hasDiscount && (
          <div className="absolute top-4 left-4 bg-[#C29B57] text-white text-xs px-3 py-1 uppercase tracking-wider z-10 font-semibold" data-testid="sale-badge">{discountPercentage}% OFF</div>
        )}
        <img src={product.images?.length > 0 ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${product.images[0]}` : 'https://images.unsplash.com/photo-1649793061426-b36b041ced21?w=400'} alt={`${product.name} - ${product.category} by Studio Crochet`} className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-105" loading="lazy" width="400" height="533" data-testid="product-image" />
      </div>
      <div className="flex flex-col gap-1.5">
        <h3 className="font-cormorant text-xl text-[#1A1A18]" data-testid="product-name">{product.name}</h3>
        <p className="font-outfit text-xs uppercase tracking-wider text-[#8C8577]" data-testid="product-category">{product.category}</p>
        {rating && (
          <div className="flex items-center gap-1.5" data-testid="card-rating">
            <div className="flex items-center gap-0.5">
              {[1, 2, 3, 4, 5].map((s) => (
                <Star key={s} className={`w-3 h-3 ${s <= Math.round(rating.average_rating) ? 'fill-[#C29B57] text-[#C29B57]' : 'text-[#EBE4D5]'}`} />
              ))}
            </div>
            <span className="font-outfit text-xs text-[#8C8577]">({rating.total_reviews})</span>
          </div>
        )}
        <div className="flex items-center gap-2">
          <span className="font-outfit text-lg text-[#1A1A18]" data-testid="product-price">{formatPrice(displayPrice)}</span>
          {hasDiscount && (
            <span className="font-outfit text-sm text-[#8C8577] line-through" data-testid="original-price">{formatPrice(product.price)}</span>
          )}
        </div>
        {product.stock < 10 && product.stock > 0 && (
          <span className="text-xs text-[#9E2A2B] font-outfit" data-testid="low-stock-indicator">{t('onlyLeft').replace('{count}', product.stock)}</span>
        )}
      </div>
    </Link>
  );
};

export default ProductCard;
